// src/components/ProjektVerwaltung.js
// Projekt-Verwaltungsseite

import React, { useState, useEffect } from 'react';
import { 
  getAllProjekte, 
  createProjekt, 
  updateProjekt, 
  deleteProjekt,
  getProjektMitarbeiter,
  addMitarbeiterToProjekt,
  updateMitarbeiterProjektRole,
  removeMitarbeiterFromProjekt,
  getAllMitarbeiterWithProjekte,
  ROLES,
  hasRoleOrHigher,
  sortMitarbeiterByRole,
  ROLE_HIERARCHY
} from '../services/firebaseProjektService';
import { useUser } from '../UserContext';
import './ProjektVerwaltung.css';

const ProjektVerwaltung = () => {
  const { user } = useUser();
  const [projekte, setProjekte] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Spalten-States
  const [selectedProjekt, setSelectedProjekt] = useState(null);
  const [selectedMitarbeiter, setSelectedMitarbeiter] = useState(null);
  const [spalte2Action, setSpalte2Action] = useState(null); // 'new', 'edit', 'delete', 'addMitarbeiter', 'mitarbeiterList'
  const [spalte3Action, setSpalte3Action] = useState(null); // 'edit', 'delete', 'addMitarbeiter', 'mitarbeiterDetails'
  
  // Formular-States
  const [projektForm, setProjektForm] = useState({
    name: '',
    description: '',
    status: 'aktiv'
  });
  const [mitarbeiterForm, setMitarbeiterForm] = useState({
    userId: '',
    role: ROLES.MITARBEITER
  });

  // Debug-Berechtigungen
  const userRole = user?.role || 'mitarbeiter';
  const canCreateProjekt = hasRoleOrHigher(userRole, ROLES.OBJEKTLEITER);
  const canEditProjekt = hasRoleOrHigher(userRole, ROLES.PROJEKTLEITER);
  const canDeleteProjekt = hasRoleOrHigher(userRole, ROLES.NIEDERLASSUNGSLEITER);
  const canManageMitarbeiter = hasRoleOrHigher(userRole, ROLES.PROJEKTLEITER);



  const loadData = async () => {
    try {
      setLoading(true);
      const [projekteData, mitarbeiterData] = await Promise.all([
        getAllProjekte(),
        getAllMitarbeiterWithProjekte()
      ]);
      
      setProjekte(projekteData);
      setMitarbeiter(mitarbeiterData);
      console.log(`${projekteData.length} Projekte geladen`);
      console.log(`${mitarbeiterData.length} Mitarbeiter mit Projekten geladen`);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Spalte 1: Projektliste
  const renderSpalte1 = () => (
    <div className="spalte spalte-1">
      <h3>Projekte</h3>
      
      {/* Neues Projekt Button */}
      <button 
        className="btn btn-primary new-projekt-btn"
        onClick={() => {
          setSelectedProjekt(null);
          setSpalte2Action('new');
          setSpalte3Action(null);
          setSelectedMitarbeiter(null);
        }}
      >
        ➕ Neues Projekt
      </button>
      
      {/* Projektliste */}
      <div className="projekt-liste">
        {projekte.map((projekt) => (
          <div 
            key={projekt.id} 
            className={`projekt-item ${selectedProjekt?.id === projekt.id ? 'selected' : ''}`}
            onClick={() => {
              setSelectedProjekt(projekt);
              setSpalte2Action('details');
              setSpalte3Action(null);
              setSelectedMitarbeiter(null);
            }}
          >
            <span className="projekt-name">{projekt.name}</span>
            <span className={`projekt-status ${projekt.status}`}>
              {projekt.status === 'aktiv' ? '🟢' : '🔴'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  // Spalte 2: Projekt-Aktionen
  const renderSpalte2 = () => {
    if (spalte2Action === 'new') {
      return (
        <div className="spalte spalte-2">
          <h3>Neues Projekt erstellen</h3>
          <form onSubmit={handleProjektSubmit}>
            <div className="form-group">
              <label>Projektname:</label>
              <input
                type="text"
                value={projektForm.name}
                onChange={(e) => setProjektForm({...projektForm, name: e.target.value})}
                required
                placeholder="Projektname eingeben..."
              />
            </div>
            <div className="form-group">
              <label>Beschreibung:</label>
              <textarea
                value={projektForm.description}
                onChange={(e) => setProjektForm({...projektForm, description: e.target.value})}
                placeholder="Beschreibung (optional)..."
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Status:</label>
              <select
                value={projektForm.status}
                onChange={(e) => setProjektForm({...projektForm, status: e.target.value})}
              >
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Erstellen</button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setSpalte2Action(null);
                  setProjektForm({ name: '', description: '', status: 'aktiv' });
                }}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      );
    }

    if (spalte2Action === 'details' && selectedProjekt) {
      return (
        <div className="spalte spalte-2">
          <h3>Projekt: {selectedProjekt.name}</h3>
          
          {/* Projekt-Status */}
          <div className="projekt-status-section">
            <label>Status:</label>
            <select
              value={selectedProjekt.status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="aktiv">Aktiv</option>
              <option value="inaktiv">Inaktiv</option>
            </select>
          </div>
          
          {/* Projekt-Aktionen */}
          <div className="projekt-actions-section">
            <h4>Aktionen:</h4>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setSpalte3Action('edit');
                setProjektForm({
                  name: selectedProjekt.name,
                  description: selectedProjekt.description || '',
                  status: selectedProjekt.status || 'aktiv'
                });
              }}
            >
              ✏️ Bearbeiten
            </button>
            
            <button 
              className="btn btn-danger"
              onClick={() => setSpalte3Action('delete')}
            >
              🗑️ Löschen
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={() => setSpalte3Action('addMitarbeiter')}
            >
              👥 Mitarbeiter hinzufügen
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={() => setSpalte3Action('mitarbeiterList')}
            >
              👥 Mitarbeiter
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="spalte spalte-2">
        <h3>Projekt auswählen</h3>
        <p>Wählen Sie ein Projekt aus der linken Spalte aus.</p>
      </div>
    );
  };

  // Spalte 3: Aktions-Formulare
  const renderSpalte3 = () => {
    if (spalte3Action === 'edit') {
      return (
        <div className="spalte spalte-3">
          <h3>Projekt bearbeiten</h3>
          <form onSubmit={handleProjektUpdate}>
            <div className="form-group">
              <label>Projektname:</label>
              <input
                type="text"
                value={projektForm.name}
                onChange={(e) => setProjektForm({...projektForm, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Beschreibung:</label>
              <textarea
                value={projektForm.description}
                onChange={(e) => setProjektForm({...projektForm, description: e.target.value})}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Status:</label>
              <select
                value={projektForm.status}
                onChange={(e) => setProjektForm({...projektForm, status: e.target.value})}
              >
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Aktualisieren</button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setSpalte3Action(null)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      );
    }

    if (spalte3Action === 'delete') {
      return (
        <div className="spalte spalte-3">
          <h3>Projekt löschen</h3>
          <div className="delete-confirmation">
            <p>Sind Sie sicher, dass Sie das Projekt <strong>"{selectedProjekt?.name}"</strong> löschen möchten?</p>
            <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="form-actions">
              <button 
                className="btn btn-danger"
                onClick={handleProjektDelete}
              >
                Ja, löschen
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setSpalte3Action(null)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (spalte3Action === 'addMitarbeiter') {
      return (
        <div className="spalte spalte-3">
          <h3>Mitarbeiter hinzufügen</h3>
          <form onSubmit={handleMitarbeiterSubmit}>
            <div className="form-group">
              <label>Mitarbeiter:</label>
              <select
                value={mitarbeiterForm.userId}
                onChange={(e) => setMitarbeiterForm({...mitarbeiterForm, userId: e.target.value})}
                required
              >
                <option value="">Mitarbeiter auswählen</option>
                {mitarbeiter
                  .filter(m => !m.projekte.some(p => p.projektId === selectedProjekt?.id))
                  .map(m => (
                    <option key={m.id} value={m.id}>
                      {m.name || m.username}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="form-group">
              <label>Rolle:</label>
              <select
                value={mitarbeiterForm.role}
                onChange={(e) => setMitarbeiterForm({...mitarbeiterForm, role: e.target.value})}
              >
                {Object.values(ROLES).map(role => (
                  <option key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Hinzufügen</button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setSpalte3Action(null)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      );
    }

    if (spalte3Action === 'mitarbeiterList') {
      const projektMitarbeiter = getProjektMitarbeiter(selectedProjekt?.id);
      
      return (
        <div className="spalte spalte-3">
          <h3>Projekt-Mitarbeiter</h3>
          <div className="mitarbeiter-liste">
            {projektMitarbeiter.map((mitarbeiter) => {
              const zuordnung = mitarbeiter.projekte.find(p => p.projektId === selectedProjekt?.id);
              return (
                <div 
                  key={mitarbeiter.id} 
                  className={`mitarbeiter-item ${selectedMitarbeiter?.id === mitarbeiter.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedMitarbeiter(mitarbeiter);
                    setSpalte3Action('mitarbeiterDetails');
                  }}
                >
                  <span className="mitarbeiter-name">{mitarbeiter.name || mitarbeiter.username}</span>
                  <span className="mitarbeiter-role">{getRoleDisplayName(zuordnung.role)}</span>
                </div>
              );
            })}
            {projektMitarbeiter.length === 0 && (
              <p className="no-mitarbeiter">Keine Mitarbeiter zugeordnet</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="spalte spalte-3">
        <h3>Aktion auswählen</h3>
        <p>Wählen Sie eine Aktion aus der mittleren Spalte aus.</p>
      </div>
    );
  };

  // Spalte 4: Mitarbeiter-Details
  const renderSpalte4 = () => {
    if (spalte3Action === 'mitarbeiterDetails' && selectedMitarbeiter && selectedProjekt) {
      const zuordnung = selectedMitarbeiter.projekte.find(p => p.projektId === selectedProjekt.id);
      
      return (
        <div className="spalte spalte-4">
          <h3>Mitarbeiter-Details</h3>
          <div className="mitarbeiter-details">
            <div className="detail-group">
              <label>Name:</label>
              <span>{selectedMitarbeiter.name || selectedMitarbeiter.username}</span>
            </div>
            
            <div className="detail-group">
              <label>Aktuelle Rolle:</label>
              <select
                value={zuordnung.role}
                onChange={(e) => handleRoleChange(zuordnung.id, e.target.value)}
              >
                {Object.values(ROLES).map(role => (
                  <option key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="detail-group">
              <label>E-Mail:</label>
              <span>{selectedMitarbeiter.email || 'Nicht verfügbar'}</span>
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn-danger"
                onClick={() => handleRemoveMitarbeiter(zuordnung.id)}
              >
                🗑️ Vom Projekt entfernen
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="spalte spalte-4">
        <h3>Mitarbeiter auswählen</h3>
        <p>Wählen Sie einen Mitarbeiter aus der dritten Spalte aus.</p>
      </div>
    );
  };

  // Event Handler
  const handleProjektSubmit = async (e) => {
    e.preventDefault();
    try {
      await createProjekt(projektForm);
      setProjektForm({ name: '', description: '', status: 'aktiv' });
      setSpalte2Action(null);
      loadData();
    } catch (error) {
      console.error('Fehler beim Erstellen des Projekts:', error);
      setError(error.message);
    }
  };

  const handleProjektUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateProjekt(selectedProjekt.id, projektForm);
      setSpalte3Action(null);
      loadData();
      setSelectedProjekt({...selectedProjekt, ...projektForm});
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Projekts:', error);
      setError(error.message);
    }
  };

  const handleProjektDelete = async () => {
    try {
      await deleteProjekt(selectedProjekt.id);
      setSpalte3Action(null);
      setSpalte2Action(null);
      setSelectedProjekt(null);
      loadData();
    } catch (error) {
      console.error('Fehler beim Löschen des Projekts:', error);
      setError(error.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateProjekt(selectedProjekt.id, { status: newStatus });
      setSelectedProjekt({...selectedProjekt, status: newStatus});
      loadData();
    } catch (error) {
      console.error('Fehler beim Ändern des Status:', error);
      setError(error.message);
    }
  };

  const handleMitarbeiterSubmit = async (e) => {
    e.preventDefault();
    try {
      await addMitarbeiterToProjekt(selectedProjekt.id, mitarbeiterForm.userId, mitarbeiterForm.role);
      setMitarbeiterForm({ userId: '', role: ROLES.MITARBEITER });
      setSpalte3Action(null);
      loadData();
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Mitarbeiters:', error);
      setError(error.message);
    }
  };

  const handleRoleChange = async (zuordnungId, newRole) => {
    try {
      await updateMitarbeiterProjektRole(zuordnungId, newRole);
      loadData();
    } catch (error) {
      console.error('Fehler beim Ändern der Rolle:', error);
      setError(error.message);
    }
  };

  const handleRemoveMitarbeiter = async (zuordnungId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Mitarbeiter vom Projekt entfernen möchten?')) {
      return;
    }
    
    try {
      await removeMitarbeiterFromProjekt(zuordnungId);
      setSelectedMitarbeiter(null);
      setSpalte3Action('mitarbeiterList');
      loadData();
    } catch (error) {
      console.error('Fehler beim Entfernen des Mitarbeiters:', error);
      setError(error.message);
    }
  };

  // Hilfsfunktionen
  const getProjektMitarbeiter = (projektId) => {
    const projektMitarbeiter = mitarbeiter.filter(m => 
      m.projekte.some(p => p.projektId === projektId)
    );
    
    return sortMitarbeiterByRole(projektMitarbeiter.map(m => {
      const projektZuordnung = m.projekte.find(p => p.projektId === projektId);
      return {
        ...m,
        projektRole: projektZuordnung ? projektZuordnung.role : ROLES.MITARBEITER
      };
    }));
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      [ROLES.SUPERADMIN]: 'SuperAdmin',
      [ROLES.ADMIN]: 'Administrator',
      [ROLES.NIEDERLASSUNGSLEITER]: 'Niederlassungsleiter',
      [ROLES.OBJEKTLEITER]: 'Objektleiter',
      [ROLES.PROJEKTLEITER]: 'Projektleiter',
      [ROLES.SCHICHTLEITER]: 'Schichtleiter',
      [ROLES.TEAMLEITER]: 'Teamleiter',
      [ROLES.MITARBEITER]: 'Mitarbeiter'
    };
    return roleNames[role] || role;
  };

  if (loading) {
    return (
      <div className="projekt-verwaltung">
        <div className="loading-container">
          <span>Lade Projektverwaltung...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="projekt-verwaltung">
      <div className="projekt-verwaltung-header">
        <h2>Projektverwaltung</h2>
      </div>

      {error && (
        <div className="error-message">
          <span>{error}</span>
        </div>
      )}

      <div className="spalten-layout">
        {renderSpalte1()}
        {renderSpalte2()}
        {renderSpalte3()}
        {renderSpalte4()}
      </div>
    </div>
  );
};

export default ProjektVerwaltung;
